import { useCallback, useMemo } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useTemplateList } from './queries';
import { useUpdateTemplate, useReorderTemplate } from './mutations';
import { useTemplateModals } from './use-template-modals';
import { pricingTiersToPlanTypes } from './utils';
import type { ProjectTemplateInfoResponseModel } from '@uniquegood/realworld-admin-interface';
import type { PricingTier } from './utils';
import { STUDIO_HOST } from '@src/config';

/**
 * 템플릿 페이지 전역 컨트롤러
 *
 * @description
 * - 템플릿 목록 조회, 수정, 순서 변경을 한 곳에서 관리합니다.
 * - 드래그 앤 드롭으로 순서 변경을 처리합니다.
 */
export function useTemplateController() {
  const templateQuery = useTemplateList();
  const updateTemplateMutation = useUpdateTemplate();
  const reorderTemplateMutation = useReorderTemplate();
  const modals = useTemplateModals();

  const templates = useMemo(
    () => templateQuery.data ?? [],
    [templateQuery.data]
  );

  // 요금제 업데이트 핸들러
  const handlePricingTierUpdate = useCallback(
    async (templateId: string, tiers: PricingTier[]) => {
      const planTypes = pricingTiersToPlanTypes(tiers);
      await updateTemplateMutation.mutateAsync({
        templateId,
        body: { planTypes },
      });
    },
    [updateTemplateMutation]
  );

  // 드래그 앤 드롭 종료 핸들러
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = templates.findIndex((item) => item.id === active.id);
      const newIndex = templates.findIndex((item) => item.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const newItems = arrayMove(templates, oldIndex, newIndex);
      const targetTemplate = newItems[newIndex];

      // 순서 변경 API 호출 (낙관적 업데이트를 위해 reorderedItems 전달)
      await reorderTemplateMutation.mutateAsync({
        templateId: targetTemplate.id,
        newOrder: newIndex + 1,
        reorderedItems: newItems,
      });
    },
    [templates, reorderTemplateMutation]
  );

  // 스튜디오 열기 핸들러
  const handleStudioOpen = useCallback((templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    // TODO: studioUrl이 API 응답에 포함되는지 확인 필요
    // if (template?.studioUrl) {
      window.open(`https://${STUDIO_HOST}/apps/${template?.applicationId}/projects/${template?.projectId}`, '_blank');
    // }
  }, [templates]);

  // 요금제 설정 모달 열기
  const openPricingTierModal = useCallback(
    (template: ProjectTemplateInfoResponseModel) => {
      modals.openPricingTierModal(template, (tiers) => {
        handlePricingTierUpdate(template.id, tiers);
      });
    },
    [modals, handlePricingTierUpdate]
  );

  // 콘텐츠 상세 모달 열기
  const openContentDetailModal = useCallback(
    (template: ProjectTemplateInfoResponseModel) => {
      modals.openContentDetailModal(template);
    },
    [modals]
  );

  return {
    // 데이터
    templates,
    isLoading: templateQuery.isLoading,
    isFetching: templateQuery.isFetching,
    error: templateQuery.error,

    // 액션
    handleDragEnd,
    handlePricingTierUpdate,
    handleStudioOpen,
    openPricingTierModal,
    openContentDetailModal,

    // Mutation 상태
    isUpdating: updateTemplateMutation.isPending,
    isReordering: reorderTemplateMutation.isPending,
  };
}

